import React, { useState } from "react";
import { Brain, Maximize2, TrendingUp } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

const RATING_COLORS = {
  "Outstanding":         "#00ff90",
  "Exceeds":             "#34d399",
  "Meets":               "#60a5fa",
  "Below":               "#fbbf24",
  "Unsatisfactory":      "#f87171",
};

const NINEBOX_LABELS = [
  ["⭐ Star","🔥 High Potential","🚀 Future Leader"],
  ["✔ Solid","💎 Core Player","📈 Growth Potential"],
  ["⚠ Risk","🔄 Needs Support","✅ Reliable"],
];

export default function PerformanceWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");

  const ratings   = data.rating_distribution || [];
  const depts     = data.by_department       || [];
  const ninebox   = data.nine_box            || {};
  const goals     = data.goals_summary       || {};

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-[#a78bfa]/60" />
          <span className="text-sm font-semibold text-white/80">Performance</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse performance across the organisation: rating distribution, review completion, goals progress, high performers and at-risk employees. Provide strategic recommendations.")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#a78bfa]/20 bg-[#a78bfa]/5 text-[#a78bfa]/60 hover:text-[#a78bfa] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors"><Maximize2 size={13} /></button>}
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview","ratings","9-box","goals","departments"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#a78bfa]/10 text-[#a78bfa] border border-[#a78bfa]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Review Completion", value: data.review_completion ? `${data.review_completion}%` : "—", color: data.review_completion >= 80 ? "#00ff90" : "#fbbf24" },
                  { label: "Avg Rating",         value: data.avg_rating ? data.avg_rating.toFixed(1) : "—", color: "#a78bfa" },
                  { label: "High Performers",    value: data.high_performers ?? "—", color: "#00ff90" },
                  { label: "At Risk",            value: data.at_risk_count ?? "—",   color: data.at_risk_count > 0 ? "#f87171" : "#34d399" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/30 mb-1">{label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Completion progress */}
              {data.review_completion != null && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-white/30 uppercase tracking-wider">Cycle Progress</p>
                    <span className="text-xs font-mono text-[#a78bfa]">{data.reviews_submitted ?? 0} / {data.reviews_total ?? 0}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#a78bfa]/60" style={{ width: `${data.review_completion || 0}%` }} />
                  </div>
                  {data.reviews_pending > 0 && (
                    <p className="text-xs text-yellow-400/60 mt-2">
                      {data.reviews_pending} manager reviews still pending
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "ratings" && (
            <div className="space-y-3">
              {ratings.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No rating data for this period.</p>
              ) : (
                <>
                  {/* Bell curve bars */}
                  <div className="flex items-end gap-1 h-24">
                    {ratings.map((r, i) => {
                      const max = Math.max(...ratings.map(x => x.count));
                      const pct = max > 0 ? (r.count / max) * 100 : 0;
                      const color = RATING_COLORS[r.label] || "#a78bfa";
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white/60 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            {r.count} employees
                          </div>
                          <div className="w-full rounded-t-sm" style={{ height: `${Math.max(pct, 5)}%`, background: `${color}60` }} />
                          <span className="text-[9px] text-white/25 text-center leading-tight">{r.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="space-y-2 mt-4">
                    {ratings.map((r, i) => {
                      const total = ratings.reduce((s, x) => s + x.count, 0);
                      const pct   = total > 0 ? Math.round((r.count / total) * 100) : 0;
                      const color = RATING_COLORS[r.label] || "#a78bfa";
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-white/40 w-28 truncate">{r.label}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-white/5">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, opacity: 0.6 }} />
                          </div>
                          <span className="text-xs font-mono text-white/40 w-8 text-right">{r.count}</span>
                          <span className="text-xs text-white/25 w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "9-box" && (
            <div className="space-y-3">
              <p className="text-xs text-white/30">Performance vs Potential matrix</p>
              {Object.keys(ninebox).length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No 9-box data available.</p>
              ) : (
                <div className="grid grid-cols-3 gap-1">
                  {[2,1,0].map(row => (
                    [0,1,2].map(col => {
                      const key = `${row}-${col}`;
                      const count = ninebox[key] || 0;
                      const label = NINEBOX_LABELS[2-row][col];
                      const isHigh = row === 2 && col === 2;
                      const isLow  = row === 0 && col === 0;
                      return (
                        <div key={key} className={`rounded-lg border p-3 text-center ${
                          isHigh ? "border-[#00ff90]/30 bg-[#00ff90]/5" :
                          isLow  ? "border-red-500/20 bg-red-500/5" :
                          "border-white/5 bg-white/3"
                        }`}>
                          <p className="text-lg font-bold font-mono text-white/70">{count}</p>
                          <p className="text-[9px] text-white/30 mt-0.5 leading-tight">{label}</p>
                        </div>
                      );
                    })
                  ))}
                </div>
              )}
              <div className="flex justify-between text-[10px] text-white/20 px-1">
                <span>← Low Performance</span>
                <span>High Performance →</span>
              </div>
            </div>
          )}

          {tab === "goals" && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: "Total Goals",  value: goals.total,     color: "#a78bfa" },
                  { label: "Completed",    value: goals.completed,  color: "#00ff90" },
                  { label: "At Risk",      value: goals.at_risk,    color: "#f87171" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3 text-center">
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value ?? "—"}</p>
                    <p className="text-xs text-white/30 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {goals.completion_rate != null && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-white/30">Goals Completion Rate</p>
                    <span className="text-sm font-mono text-[#a78bfa]">{goals.completion_rate}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#a78bfa]/60" style={{ width: `${goals.completion_rate}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              {depts.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No department data.</p>
              ) : depts.sort((a,b) => (b.avg_rating||0) - (a.avg_rating||0)).map((d, i) => {
                const color = (d.avg_rating >= 4) ? "#00ff90" : (d.avg_rating >= 3) ? "#60a5fa" : "#f87171";
                return (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                    <span className="text-sm text-white/60">{d.dept}</span>
                    <div className="flex gap-4 text-xs font-mono">
                      <span style={{ color }}>{d.avg_rating?.toFixed(1) ?? "—"} avg</span>
                      <span className="text-white/30">{d.completion}% done</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}